import { useState } from 'react'
import { supabase } from '../lib/supabase'

const MEASUREMENT_COLUMNS =
	'timestamp, total_alkalinity_ppm_rolling_avg, ph, temperature_c, conductivity_mscm'

const AGGREGATED_INTERVALS = new Set(['10min', '60min', '1day', '1week'])

/**
 * Fetch historical measurements for a sensor.
 * @param {string|number} customerId
 * @param {string} sensorId
 * @param {string} startTime ISO timestamp
 * @param {string} endTime ISO timestamp
 * @param {string} [interval='raw'] Aggregation interval key (raw | 10min | 60min | 1day | 1week)
 */
export function useHistoricalData() {
	const [data, setData] = useState(null)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)

	const fetchData = async (customerId, sensorId, startTime, endTime, interval = 'raw') => {
		setLoading(true)
		setError(null)

		try {
			if (AGGREGATED_INTERVALS.has(interval)) {
				const { data: measurements, error: rpcError } = await supabase.rpc(
					'get_aggregated_measurements',
					{
						p_customer_id: customerId,
						p_sensor_id: sensorId,
						p_start: startTime,
						p_end: endTime,
						p_interval: interval,
					}
				)

				if (rpcError) throw rpcError
				setData({ data: measurements || [], interval })
				return
			}

			// Raw: keyset pagination on timestamp (PostgREST default max 1000 rows)
			let allMeasurements = []
			let hasMore = true
			let cursor = null
			const batchSize = 1000

			while (hasMore) {
				let query = supabase
					.from('aggregated_measurements')
					.select(MEASUREMENT_COLUMNS)
					.eq('customer_id', customerId)
					.eq('sensor_id', sensorId)
					.gte('timestamp', startTime)
					.lte('timestamp', endTime)
					.order('timestamp', { ascending: true })
					.limit(batchSize)

				if (cursor) {
					query = query.gt('timestamp', cursor)
				}

				const { data: measurements, error: queryError } = await query

				if (queryError) throw queryError

				if (measurements && measurements.length > 0) {
					allMeasurements = allMeasurements.concat(measurements)
					cursor = measurements[measurements.length - 1].timestamp
					hasMore = measurements.length === batchSize
				} else {
					hasMore = false
				}
			}

			setData({ data: allMeasurements, interval: 'raw' })
		} catch (err) {
			console.error('Error fetching historical data:', err)
			setError(err.message)
		} finally {
			setLoading(false)
		}
	}

	const clearData = () => {
		setData(null)
		setError(null)
	}

	return { data, loading, error, fetchData, clearData }
}
